import Container from '@atoms/Container';
import PageTitle from '@atoms/PageTitle';
import SvgSafeGatsbyImage from '@atoms/SvgSafeGatsbyImage';
import Layout from '@components/Layout';
import { BREAKPOINT_SM } from '@constants/constants';
import { useLocation } from '@reach/router';
import Breadcrumbs from '@sections/Breadcrumbs';
import apCaseOnlyTitleTags from '@utils/apCaseOnlyTitleTags';
import format from 'date-fns/format';
import { graphql } from 'gatsby';
import React, { useState } from 'react';
import NearestVertical from '../components/franchise/nearest-vertical';
import Divider from '../components/primitives/divider';
import Col from '../components/primitives/grid/col';
import Row from '../components/primitives/grid/row';
import { breakpoints } from '../components/primitives/tokens';
import RichTextEditorStyles from '../components/utils/RichTextEditorStyles';
import Seo from '../components/utils/Seo';
import CtaProvider from '../contexts/franchise/cta-provider';
import FacebookIcon from '../images/svg/article-fb.svg';
import LinkIcon from '../images/svg/article-link.svg';
import TwitterIcon from '../images/svg/article-twitter.svg';
import Link from "../components/primitives/link";
import { colors } from '../components/primitives/colors';

const ArticlePage = ({ data, pageContext }) => {
    const {
        title,
        show_date,
        authors,
        content,
        featured_image,
        publish_details,
        sidebar_widgets,
        seo,
    } = data.cmsArticle || data.cmsPressRelease || {};
    const { hasLocationWidget } = pageContext;
    const { time: publish_time } = publish_details;
    const location = useLocation();
    const [CopyAlertVisible, setCopyAlertVisible] = useState(false);

    React.useEffect(() => {
        if (Number(document.documentElement.clientWidth) > BREAKPOINT_SM) {
            document.body.style.overflowX = '';
            document.documentElement.style.overflowX = '';
        }
    }, []);

    return (
        <CtaProvider>
            <Layout>
                <Seo
                    title={seo?.meta_title || title}
                    description={seo?.meta_description}
                    imageUrl={seo?.meta_image?.url}
                />
                <Container css={{
                    marginTop: '24px'
                }}>
                    <Breadcrumbs />
                    <PageTitle css={styles.pageTitle}>
                        {title || 'Untitled Article'}
                    </PageTitle>
                    <div css={styles.articleMeta}>
                        {!!show_date && !!publish_time && (
                            <p>
                                {format(new Date(publish_time), 'yyyy-MM-dd k:mm').toString()}
                            </p>
                        )}
                        {!!authors && (
                            <p>
                                By
                                {' '}
                                {authors}
                            </p>
                        )}
                    </div>
                    <Divider />
                    <div>
                        <div css={{
                            display: 'flex',
                            alignItems: 'center',
                            margin: '16px 0',
                        }}
                        >
                            <span css={styles.share}>Share: </span>
                            <li css={styles.shareLinkItem}>
                                <Link css={styles.shareLink}
                                    to={`http://www.facebook.com/sharer.php?u=${location.href}`}
                                    target="_blank"
                                >
                                    <FacebookIcon />
                                </Link>
                            </li>
                            <li css={styles.shareLinkItem}>
                                <Link css={styles.shareLink}
                                    to={`http://twitter.com/share?text=${location.href}`}
                                    target="_blank"
                                >
                                    <TwitterIcon />
                                </Link>
                            </li>
                            <li css={styles.shareLinkItem}>
                                <Link css={styles.shareLink}
                                    onClick={() => {
                                        setCopyAlertVisible(true);
                                        navigator.clipboard.writeText(location.href);
                                        setTimeout(() => setCopyAlertVisible(false), 700);
                                    }}
                                >
                                    <LinkIcon />
                                </Link>
                                <span css={styles.alert(CopyAlertVisible)}>Copied!</span>
                            </li>
                        </div>
                    </div>
                    <Divider css={{
                        marginBottom: '24px',
                    }}
                    />
                    <Row>
                        <Col breakpoints={{
                            dt: { span: hasLocationWidget ? 8 : 12 },
                            tb: { span: 12 },
                        }}
                        >
                            <article css={styles.article}>
                                {featured_image?.url && (
                                    <div css={{
                                        marginBottom: '24px',
                                    }}
                                    >
                                        <SvgSafeGatsbyImage
                                            image={featured_image}
                                            alt={featured_image.description}
                                            css={styles.featuredImage}
                                        />
                                        <Divider />
                                    </div>
                                )}
                                <div
                                    css={[RichTextEditorStyles, styles.copy]}
                                    dangerouslySetInnerHTML={{ __html: content }}
                                />
                                <Divider css={{
                                    marginBottom: '24px',
                                    [breakpoints.tb]: {
                                        marginBottom: '32px',
                                    },
                                }}
                                />
                            </article>
                        </Col>
                        {hasLocationWidget && (
                            <Col breakpoints={{
                                dt: { span: 4 },
                                tb: { span: 12 },
                            }}
                            >
                                <div css={{
                                    position: 'sticky',
                                    top: '100px',
                                    paddingBottom: '24px',
                                    [breakpoints.tb]: {
                                        paddingBottom: '32px',
                                    },
                                }}
                                >
                                    <NearestVertical />

                                    {!!sidebar_widgets
                                        && sidebar_widgets.map(sb => (
                                            <div css={styles.widget} key={sb.heading}>
                                                <h2 css={styles.widgetHeading}>
                                                    {apCaseOnlyTitleTags(sb.heading)}
                                                </h2>
                                                <p css={styles.widgetCopy}>{sb.copy}</p>
                                            </div>
                                        ))}
                                </div>
                            </Col>
                        )}
                    </Row>
                </Container>
            </Layout>
        </CtaProvider>
    );
};

const styles = {
    share: {
        marginRight: "1rem",
        fontWeight: 600

    },
    pageTitle: {
        marginBottom: "0.75rem",
        "@media (min-width: 1024px)": {
            width: "66.666667%"
        },
        "@media (min-width: 1280px)": {
            width: "60%"
        }
    },
    article: {
        "@media (min-width: 1024px)": {
            flex: "1 1 auto"
        }
    },
    articleMeta: {
        marginBottom: "2.5rem",
        fontSize: "1.125rem",
        lineHeight: "1.75rem"
    },
    shareLinkItem: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center"
    },
    shareLink: {
        display: "inline-flex",
        paddingTop: "0.5rem",
        paddingBottom: "0.5rem",
        paddingLeft: "0.75rem",
        paddingRight: "0.75rem",
        alignItems: "center",
        color: colors.primaryOrange,
    },
    copy: {
        paddingBottom: "2rem",
        fontSize: "1.25rem",
        lineHeight: "1.75rem"
    },
    featuredImage: {
        width: "100%",
        marginBottom: '24px'
    },
    widget: {
        padding: "2rem",
        marginTop: "2.5rem",
        marginBottom: "2.5rem"
    },
    widgetHeading: {
        fontSize: "1.5rem",
        lineHeight: "2rem",
        fontWeight: 400
    },
    widgetCopy: {
        paddingTop: "0.5rem",
        paddingBottom: "0.5rem"
    },
    alert: (visible) => ({
        display: "block",
        backgroundColor: colors.shadesBlack,
        padding: "0.5rem",
        marginLeft: "0.5rem",
        borderRadius: "0.25rem",
        color: colors.shadesWhite,
        transitionProperty: "opacity",
        transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
        transitionDuration: "300ms",
        opacity: visible ? 1 : 0,
    })
};

export const query = graphql`
  query ($slug: String!) {
    cmsArticle(url: { eq: $slug }) {
      title
      url
      show_date
      authors
      content
      featured_image {
        filename
        title
        description
        url
        contentStackAsset(width: 1440)
      }
      publish_details {
        time
      }
      settings {
        is_featured
        service_line {
          title
          icon {
            filename
            title
            url
            contentStackAsset(width: 1440)
          }
        }
      }
      seo {
        meta_title
        meta_description
        meta_image {
          url
        }
      }
      sidebar_widgets {
        copy
        heading
      }
    }
    cmsPressRelease(url: { eq: $slug }) {
      title
      url
      show_date
      authors
      content
      featured_image {
        filename
        title
        description
        url
        contentStackAsset(width: 1440)
      }
      publish_details {
        time
      }
      seo {
        meta_title
        meta_description
        meta_image {
          url
        }
      }
      sidebar_widgets {
        copy
        heading
      }
    }
  }
`;

export default ArticlePage;
